'use client'

import React from 'react'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { Box, Button, Divider, FormControlLabel, IconButton, Typography } from '@mui/material'

import { ToggleSwitch } from '@/src/components/common/ui/toggleSwitch'
import { MultiCitySegment } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import { TranslationFunction } from '@/src/types/translations'

export type { MultiCitySegment }

const cardSx = {
    border: 1,
    borderColor: 'grey.200',
    borderRadius: 2,
    bgcolor: 'background.paper',
    overflow: 'hidden'
}

interface MultiCityProps {
    segments: MultiCitySegment[]
    segmentsWithSameAirportError: Set<number>
    onSegmentFieldClick: (segmentIndex: number, field: 'from' | 'to' | 'date') => void
    onSwapClick: (segmentIndex: number) => void
    onAddMoreClick: () => void
    onRemoveSegment: (segmentIndex: number) => void
    onTravelersClick: () => void
    onSearchClick: () => void
    formatDateDisplay: (date: Date | null) => string
    formatTravelersDisplay: () => string
    flightClass: string
    canAddMore: boolean
    isNonStop: boolean
    onNonStopChange: (checked: boolean) => void
    searchButtonLabel?: string
    searchButtonVariant?: 'contained' | 'customVariantPrimary'
    searchButtonDisabled?: boolean
    showNonStop?: boolean
    hideSearchButton?: boolean
    compactTypography?: boolean
    t: TranslationFunction<'common'>
}

interface AirportFieldProps {
    label: string
    value: string
    placeholder: string
    hasError: boolean
    align?: 'left' | 'right'
    compactTypography?: boolean
    onClick: () => void
}

const AirportField: React.FC<AirportFieldProps> = ({
    label,
    value,
    placeholder,
    hasError,
    align = 'left',
    compactTypography = false,
    onClick
}) => (
    <Box
        onClick={onClick}
        sx={{
            minWidth: 0,
            width: '100%',
            textAlign: align,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
            borderRadius: 1
        }}
    >
        <Typography
            sx={{
                color: 'text.secondary',
                fontWeight: 500,
                display: 'block',
                fontSize: compactTypography ? 12 : undefined
            }}
            variant={compactTypography ? undefined : 'caption'}
        >
            {label}
        </Typography>
        <Typography
            component="div"
            sx={{
                fontWeight: value ? 600 : 400,
                fontSize: compactTypography ? 14 : undefined,
                color: hasError ? 'error.main' : value ? 'text.primary' : 'grey.500',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%'
            }}
            variant={compactTypography ? undefined : 'body1'}
        >
            {value || placeholder}
        </Typography>
    </Box>
)

const MultiCity: React.FC<MultiCityProps> = ({
    segments,
    segmentsWithSameAirportError,
    onSegmentFieldClick,
    onSwapClick,
    onAddMoreClick,
    onRemoveSegment,
    onTravelersClick,
    onSearchClick,
    formatDateDisplay,
    formatTravelersDisplay,
    flightClass,
    canAddMore,
    isNonStop,
    onNonStopChange,
    searchButtonLabel,
    searchButtonVariant,
    searchButtonDisabled = false,
    showNonStop = true,
    hideSearchButton = false,
    compactTypography = false,
    t
}) => {
    const showRemoveSegment = segments.length > 2
    const segmentSx = compactTypography ? undefined : { pt: 2, pr: 2.5, pb: 1, pl: 2.5 }
    const sectionSx = compactTypography ? undefined : { px: 2, mb: 2 }
    const labelSx = { fontSize: 12, color: 'text.secondary', fontWeight: 500 }
    const valueSx = { fontSize: 14, fontWeight: 600 }
    const emptyValueSx = { fontSize: 14, fontWeight: 400, color: 'grey.500' }

    return (
        <Box sx={compactTypography ? { p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 } : undefined}>
            {segments.map((segment, index) => {
                const hasSameAirportError = segmentsWithSameAirportError.has(index)

                return (
                    <Box key={index} sx={segmentSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: compactTypography ? 14 : undefined
                                }}
                                variant={compactTypography ? undefined : 'body2'}
                            >
                                {t('flightNumber', { number: index + 1 })}
                            </Typography>
                            {showRemoveSegment && (
                                <IconButton
                                    onClick={() => onRemoveSegment(index)}
                                    size="small"
                                    aria-label={t('remove')}
                                    sx={{ border: 1, borderColor: 'grey.200', width: 28, height: 28 }}
                                >
                                    <RemoveIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                </IconButton>
                            )}
                        </Box>

                        <Box sx={cardSx}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
                                    alignItems: 'center',
                                    columnGap: 1,
                                    px: 1.5,
                                    py: 1.5
                                }}
                            >
                                <AirportField
                                    label={t('from')}
                                    value={segment.from.value}
                                    placeholder={t('enterOrigin')}
                                    hasError={hasSameAirportError}
                                    compactTypography={compactTypography}
                                    onClick={() => onSegmentFieldClick(index, 'from')}
                                />
                                <IconButton
                                    onClick={e => {
                                        e.stopPropagation()
                                        onSwapClick(index)
                                    }}
                                    size="small"
                                    sx={{
                                        bgcolor: 'background.paper',
                                        border: 1,
                                        borderColor: 'grey.200',
                                        width: 32,
                                        height: 32,
                                        flexShrink: 0,
                                        boxShadow: 1
                                    }}
                                >
                                    <SwapHorizIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </IconButton>
                                <AirportField
                                    label={t('to')}
                                    value={segment.to.value}
                                    placeholder={t('enterDestination')}
                                    hasError={hasSameAirportError}
                                    align="right"
                                    compactTypography={compactTypography}
                                    onClick={() => onSegmentFieldClick(index, 'to')}
                                />
                            </Box>

                            <Divider />

                            <Box
                                onClick={() => onSegmentFieldClick(index, 'date')}
                                sx={{
                                    px: 1.5,
                                    py: compactTypography ? 1.25 : 1.5,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                            >
                                <Typography
                                    sx={compactTypography ? labelSx : { color: 'text.secondary', fontWeight: 500 }}
                                    variant={compactTypography ? undefined : 'caption'}
                                >
                                    {t('departure')}
                                </Typography>
                                <Typography
                                    sx={
                                        segment.date
                                            ? compactTypography
                                                ? valueSx
                                                : { fontWeight: 600, color: 'text.primary' }
                                            : compactTypography
                                              ? emptyValueSx
                                              : { fontWeight: 400, color: 'grey.500' }
                                    }
                                    variant={compactTypography ? undefined : 'body1'}
                                >
                                    {segment.date ? formatDateDisplay(segment.date) : t('date')}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )
            })}

            {canAddMore && (
                <Box sx={sectionSx}>
                    <Button
                        onClick={onAddMoreClick}
                        variant="outlined"
                        fullWidth
                        startIcon={<AddIcon />}
                        sx={{
                            py: 1.25,
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: 'link.main',
                            color: 'link.main'
                        }}
                    >
                        {t('addMore')}
                    </Button>
                </Box>
            )}

            <Box sx={compactTypography ? undefined : sectionSx}>
                <Box
                    onClick={onTravelersClick}
                    sx={{
                        ...cardSx,
                        px: 1.5,
                        py: compactTypography ? 1.25 : 1.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                    }}
                >
                    <Typography
                        sx={compactTypography ? labelSx : { color: 'text.secondary', fontWeight: 500 }}
                        variant={compactTypography ? undefined : 'caption'}
                    >
                        {t('travellerClassHeading')}
                    </Typography>
                    <Typography
                        sx={compactTypography ? valueSx : { fontWeight: 600 }}
                        variant={compactTypography ? undefined : 'body1'}
                    >
                        {formatTravelersDisplay()} &bull; {flightClass}
                    </Typography>
                </Box>
            </Box>

            <Box sx={compactTypography ? undefined : { px: 2, py: 1, mb: 2 }}>
                {showNonStop && (
                    <FormControlLabel
                        control={
                            <ToggleSwitch
                                checked={isNonStop}
                                onChange={e => onNonStopChange(e.target.checked)}
                                size="medium"
                            />
                        }
                        label={
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {t('direct')}
                            </Typography>
                        }
                        sx={{ m: 0, gap: 1.5 }}
                    />
                )}
            </Box>

            {!hideSearchButton && (
                <Box sx={compactTypography ? undefined : { px: 2, pb: 2 }}>
                    <Button
                        fullWidth
                        variant={searchButtonVariant || 'contained'}
                        size="large"
                        disabled={searchButtonDisabled}
                        onClick={onSearchClick}
                        sx={{
                            py: compactTypography ? undefined : 1.5,
                            height: compactTypography ? 40 : undefined,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: compactTypography ? 16 : undefined
                        }}
                    >
                        {searchButtonLabel || t('searchFlights')}
                    </Button>
                </Box>
            )}
        </Box>
    )
}

export { MultiCity }
export type { MultiCityProps }
